/**
 * Education Section - "How Smart Prompt Works"
 *
 * DEVELOPMENT LOG: Phase 3 - Dynamic Content & Educational System
 * Created: Educational content explaining the process
 * Updated: December 2025 - Visual flow redesign with 3-step process
 * Status: ✅ Complete
 */

import React from 'react';
import { DebugLabel } from '../shared';

interface EducationalStep {
  icon: string;
  title: string;
  description: string;
  features: string[];
}

const educationalSteps: EducationalStep[] = [
  {
    icon: "⚡",
    title: "Compare Side-by-Side",
    description: "Run your prompt across multiple AI models simultaneously and see all responses in a clear, organized layout.",
    features: [
      "Claude, GPT-4, Gemini, and more",
      "Identical prompts to each model",
      "Real-time parallel execution"
    ]
  },
  {
    icon: "📊",
    title: "Score Responses",
    description: "Every response is evaluated across 7 key criteria to give you objective, data-driven insights.",
    features: [
      "Accuracy & factual correctness",
      "Clarity & readability",
      "Depth & thoroughness",
      "Relevance to your prompt"
    ]
  },
  {
    icon: "🎯",
    title: "Consensus Analysis",
    description: "Get a unified, best-of-breed answer that combines the strengths of all models into one trusted response.",
    features: [
      "Synthesizes top insights",
      "Highlights model agreements",
      "One clear recommendation",
      "Captures citations from responses"
    ]
  }
];

export const EducationSection: React.FC = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-800 relative">
      <DebugLabel label="EducationSection-HowItWorks" type="section" position="top-left" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            How Smart Prompt Works
          </h2>
          <p className="text-slate-300 text-xl max-w-3xl mx-auto leading-relaxed">
            Run prompts across multiple AI models, get objective scoring across 7 criteria,
            and receive one unified answer that combines the best insights from all models.
          </p>
        </div>

        {/* Steps Flow */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 relative" data-testid="how-it-works">
          {/* Connecting Arrows - Hidden on mobile */}
          <div className="hidden md:block absolute top-1/4 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-cyan-500/50 via-blue-500/50 to-purple-500/50"
               style={{ width: 'calc(50% + 3rem)', left: 'calc(16.666% + 8rem)' }} />
          <div className="hidden md:block absolute top-1/4 right-1/4 transform translate-x-1/2"
               style={{ right: 'calc(16.666% - 2rem)' }}>
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
          </div>

          {educationalSteps.map((step, index) => (
            <div
              key={index}
              className="relative group"
              data-testid={`education-step-${index + 1}`}
            >
              {/* Step Number Badge */}
              <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                {index + 1}
              </div>

              {/* Card */}
              <div className="bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-cyan-500/50 rounded-2xl p-8 h-full transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/20 hover:scale-105">
                {/* Icon */}
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-5xl" role="img" aria-label={step.title}>
                    {step.icon}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-slate-300 text-base leading-relaxed mb-6">
                  {step.description}
                </p>

                {/* Features List */}
                <ul className="space-y-2">
                  {step.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start text-sm text-slate-400"
                    >
                      <span className="text-cyan-400 mr-2 mt-0.5">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Arrow connector for mobile */}
              {index < educationalSteps.length - 1 && (
                <div className="md:hidden flex justify-center py-4">
                  <div className="text-cyan-400 text-2xl">↓</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <p className="text-slate-400 text-lg mb-6">
            Ready to find your best AI answer?
          </p>
          <a
            href="/compare"
            className="inline-block px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 transform hover:scale-105"
          >
            Try Smart Prompt Now →
          </a>
        </div>
      </div>
    </section>
  );
};